import { Alarm, AlarmSet, Class, DeletedClass } from '../types/alarm';

const ALARMS_STORAGE_KEY = 'alarms';
const ALARM_SETS_STORAGE_KEY = 'alarmSets';

// 사용자별 키 생성 함수
const getUserAlarmsKey = (userId: string) => `${ALARMS_STORAGE_KEY}_${userId}`;
const getUserAlarmSetsKey = (userId: string) => `${ALARM_SETS_STORAGE_KEY}_${userId}`;

// 사용자-클래스별 키 생성 함수
const getUserClassAlarmsKey = (userId: string, classId: string) => `${ALARMS_STORAGE_KEY}_${userId}_${classId}`;
const getUserClassAlarmSetsKey = (userId: string, classId: string) => `${ALARM_SETS_STORAGE_KEY}_${userId}_${classId}`;

export const saveAlarms = (alarms: Alarm[], userId: string) => {
  try {
    const key = getUserAlarmsKey(userId);
    localStorage.setItem(key, JSON.stringify(alarms));
  } catch (error) {
    console.error('Failed to save alarms:', error);
  }
};

export const loadAlarms = (userId: string): Alarm[] => {
  try {
    const key = getUserAlarmsKey(userId);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to load alarms:', error);
    return [];
  }
};

export const saveAlarmSets = (alarmSets: AlarmSet[], userId: string) => {
  try {
    const key = getUserAlarmSetsKey(userId);
    localStorage.setItem(key, JSON.stringify(alarmSets));
  } catch (error) {
    console.error('Failed to save alarm sets:', error);
  }
};

export const loadAlarmSets = (userId: string): AlarmSet[] => {
  try {
    const key = getUserAlarmSetsKey(userId);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to load alarm sets:', error);
    return [];
  }
};

// 사용자 데이터 삭제 함수
export const clearUserData = (userId: string) => {
  try {
    const alarmsKey = getUserAlarmsKey(userId);
    const alarmSetsKey = getUserAlarmSetsKey(userId);
    localStorage.removeItem(alarmsKey);
    localStorage.removeItem(alarmSetsKey);
  } catch (error) {
    console.error('Failed to clear user data:', error);
  }
};

// 모든 사용자 데이터 초기화 함수
export const clearAllUserData = () => {
  try {
    // 모든 localStorage 항목을 확인하여 사용자별 데이터 삭제
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(ALARMS_STORAGE_KEY) || key.startsWith(ALARM_SETS_STORAGE_KEY)) {
        localStorage.removeItem(key);
      }
    });
    console.log('모든 사용자 데이터가 초기화되었습니다.');
  } catch (error) {
    console.error('Failed to clear all user data:', error);
  }
};

// 클래스 관리 함수들
export const saveClasses = (userId: string, classes: Class[]): void => {
  try {
    localStorage.setItem(`classes_${userId}`, JSON.stringify(classes));
  } catch (error) {
    console.error('클래스 저장 중 오류:', error);
  }
};

export const loadClasses = (userId: string): Class[] => {
  try {
    const classesData = localStorage.getItem(`classes_${userId}`);
    if (classesData) {
      const classes = JSON.parse(classesData);
      return classes.map((cls: any) => ({
        ...cls,
        createdAt: new Date(cls.createdAt)
      }));
    }
  } catch (error) {
    console.error('클래스 로드 중 오류:', error);
  }
  return [];
};

export const saveCurrentClassId = (userId: string, classId: string): void => {
  try {
    localStorage.setItem(`currentClassId_${userId}`, classId);
  } catch (error) {
    console.error('현재 클래스 ID 저장 중 오류:', error);
  }
};

export const loadCurrentClassId = (userId: string): string | null => {
  try {
    return localStorage.getItem(`currentClassId_${userId}`);
  } catch (error) {
    console.error('현재 클래스 ID 로드 중 오류:', error);
  }
  return null;
};

// 클래스별 알람 저장/로드 함수들
export const saveClassAlarms = (alarms: Alarm[], userId: string, classId: string) => {
  try {
    const key = getUserClassAlarmsKey(userId, classId);
    localStorage.setItem(key, JSON.stringify(alarms));
  } catch (error) {
    console.error('Failed to save class alarms:', error);
  }
};

export const loadClassAlarms = (userId: string, classId: string): Alarm[] => {
  try {
    const key = getUserClassAlarmsKey(userId, classId);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to load class alarms:', error);
    return [];
  }
};

// 클래스별 알람 세트 저장
export const saveClassAlarmSets = (alarmSets: AlarmSet[], userId: string, classId: string) => {
  try {
    const key = getUserClassAlarmSetsKey(userId, classId);
    localStorage.setItem(key, JSON.stringify(alarmSets));
  } catch (error) {
    console.error('Failed to save class alarm sets:', error);
  }
};

// 클래스별 알람 세트 로드
export const loadClassAlarmSets = (userId: string, classId: string): AlarmSet[] => {
  try {
    const key = getUserClassAlarmSetsKey(userId, classId);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to load class alarm sets:', error);
    return [];
  }
};

// 클래스별 데이터 삭제 함수 (클래스 삭제 시 사용)
export const deleteClassData = (userId: string, classId: string) => {
  try {
    // 클래스별 알람 데이터 삭제
    const alarmsKey = getUserClassAlarmsKey(userId, classId);
    const alarmSetsKey = getUserClassAlarmSetsKey(userId, classId);
    localStorage.removeItem(alarmsKey);
    localStorage.removeItem(alarmSetsKey);
    
    console.log(`클래스 ${classId}의 로컬 데이터가 성공적으로 삭제되었습니다.`);
  } catch (error) {
    console.error('Failed to delete class data:', error);
  }
};

// 삭제된 클래스 저장
export const saveDeletedClass = (userId: string, deletedClass: DeletedClass) => {
  try {
    const key = `deletedClass_${userId}_${deletedClass.id}`;
    localStorage.setItem(key, JSON.stringify(deletedClass));
    console.log(`삭제된 클래스 ${deletedClass.name}이 임시 저장되었습니다.`);
  } catch (error) {
    console.error('Failed to save deleted class:', error);
  }
};

// 삭제된 클래스 목록 가져오기
export const loadDeletedClasses = (userId: string): DeletedClass[] => {
  try {
    const deletedClasses: DeletedClass[] = [];
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      if (key.startsWith(`deletedClass_${userId}_`)) {
        const stored = localStorage.getItem(key);
        if (stored) {
          const deletedClass = JSON.parse(stored);
          // 14일이 지나지 않은 것만 반환
          if (deletedClass.willBeDeletedAt > Date.now()) {
            deletedClasses.push({
              ...deletedClass,
              createdAt: new Date(deletedClass.createdAt)
            });
          } else {
            // 14일이 지난 것은 삭제
            localStorage.removeItem(key);
          }
        }
      }
    });
    
    return deletedClasses;
  } catch (error) {
    console.error('Failed to load deleted classes:', error);
    return [];
  }
};

// 삭제된 클래스 복구
export const restoreDeletedClass = (userId: string, classId: string): DeletedClass | null => {
  try {
    const key = `deletedClass_${userId}_${classId}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      const deletedClass = JSON.parse(stored);
      localStorage.removeItem(key);
      return {
        ...deletedClass,
        createdAt: new Date(deletedClass.createdAt)
      };
    }
    return null;
  } catch (error) {
    console.error('Failed to restore deleted class:', error);
    return null;
  }
};

// 만료된 삭제된 클래스들 정리
export const cleanupExpiredDeletedClasses = (userId: string) => {
  try {
    const keys = Object.keys(localStorage);
    let cleanedCount = 0;
    
    keys.forEach(key => {
      if (key.startsWith(`deletedClass_${userId}_`)) {
        const stored = localStorage.getItem(key);
        if (stored) {
          const deletedClass = JSON.parse(stored);
          if (deletedClass.willBeDeletedAt <= Date.now()) {
            localStorage.removeItem(key);
            cleanedCount++;
          }
        }
      }
    });
    
    if (cleanedCount > 0) {
      console.log(`${cleanedCount}개의 만료된 삭제된 클래스가 정리되었습니다.`);
    }
  } catch (error) {
    console.error('Failed to cleanup expired deleted classes:', error);
  }
}; 